import { Component, OnInit } from '@angular/core';
import { Observable } from 'rxjs';
import { SystemService } from '../../../services/system.service';
import { UploadInspectionRequestViewModel } from '../../../models/upload-inspection-admin';

@Component({
  selector: 'app-upload-inspection-admin',
  templateUrl: './upload-inspection-admin.component.html',
  styleUrls: ['./upload-inspection-admin.component.scss'],
  standalone: false
})
export class UploadInspectionAdminComponent implements OnInit {
  rows: UploadInspectionRequestViewModel[] = [];
  recordCount: number = 0;
  pageCount: number = 0;
  currentPage: number = 0;
  pageSize: number = 100;
  isLoading: boolean = false;
  sortColumn: string = 'requestedOn';
  sortDescending: boolean = true;
  searchUploadId: string = '';

  columns = [
    { name: 'uploadId', label: 'Upload Id', sortable: true },
    { name: 'requestedOn', label: 'Requested On', sortable: true },
    { name: 'requestedByUserName', label: 'User', sortable: true },
    { name: 'inspectionType', label: 'Inspection Type', sortable: true },
    { name: 'scheduledInspectionId', label: 'ScheduledInspectionId', sortable: true },
    { name: 'applicationNumber', label: 'Application Number', sortable: false },
    { name: 'attachments', label: 'Attachments', sortable: false },
    { name: 'signatures', label: 'Signatures', sortable: false },
    { name: 'details', label: 'Upload Request Details', sortable: false },
    { name: 'processedApplication', label: 'Processed Application', sortable: false },
    { name: 'status', label: 'Status', sortable: true }
  ];

  // Model properties
  showAttachmentsModal: boolean = false;
  showSignaturesModal: boolean = false;
  showDetailsModal: boolean = false;
  showResultsModal: boolean = false;
  showErrorModal: boolean = false;

  selectedAttachments: string[] = [];
  selectedSignatures: string[] = [];
  selectedDetails: UploadInspectionRequestViewModel;
  selectedResults: any;
  selectedError: string = '';

  constructor(private systemService: SystemService) {}

  ngOnInit(): void {
    this.loadData();
    this.loadRecordCount();
  }

  loadData(): void {
    this.isLoading = true;
    const skip = this.currentPage * this.pageSize;
    
    if (this.searchUploadId && this.searchUploadId.trim()) {
      this.systemService.searchUploadInspectionRequests(this.searchUploadId.trim(), skip, this.pageSize).subscribe(
        (data: UploadInspectionRequestViewModel[]) => {
          this.rows = data;
          this.isLoading = false;
        },
        (error) => {
          console.error('Error loading upload inspection requests:', error);
          this.isLoading = false;
        }
      );
    } else {
      this.systemService.getUploadInspectionRequests(skip, this.pageSize).subscribe(
        (data: UploadInspectionRequestViewModel[]) => {
          this.rows = data;
          this.isLoading = false;
        },
        (error) => {
          console.error('Error loading upload inspection requests:', error);
          this.isLoading = false;
        }
      );
    }
  }

  loadRecordCount(): void {
    if (this.searchUploadId && this.searchUploadId.trim()) {
      this.systemService.searchUploadInspectionRequestsCount(this.searchUploadId.trim()).subscribe(
        (count: number) => {
          this.recordCount = count;
          this.pageCount = Math.ceil(count / this.pageSize);
        },
        (error) => {
          console.error('Error loading upload inspection requests count:', error);
        }
      );
    } else {
      this.systemService.getUploadInspectionRequestsCount().subscribe(
        (count: number) => {
          this.recordCount = count;
          this.pageCount = Math.ceil(count / this.pageSize);
        },
        (error) => {
          console.error('Error loading upload inspection requests count:', error);
        }
      );
    }
  }

  onSearch(): void {
    this.currentPage = 0;
    this.loadData();
    this.loadRecordCount();
  }

  onClearSearch(): void {
    this.searchUploadId = '';
    this.currentPage = 0;
    this.loadData();
    this.loadRecordCount();
  }

  onPageChange(page: number): void {
    this.currentPage = page;
    this.loadData();
  }

  onSort(column: string): void {
    if (this.sortColumn === column) {
      this.sortDescending = !this.sortDescending;
    } else {
      this.sortColumn = column;
      this.sortDescending = true;
    }
    // In a real implementation, you would pass sort parameters to the API
    this.loadData();
  }

  formatDate(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    return d.toLocaleString();
  }

  getStatusClass(status: string): string {
    switch (status) {
      case 'Completed':
        return 'status-completed';
      case 'Executing':
        return 'status-executing';
      case 'Queued':
        return 'status-queued';
      case 'Collected':
        return 'status-collected';
      case 'Pending':
        return 'status-pending';
      case 'Error':
        return 'status-error';
      default:
        return 'status-pending';
    }
  }

  getScheduledInspectionIdDisplay(row: UploadInspectionRequestViewModel): string {
    if (row.scheduledInspectionId) {
      return row.scheduledInspectionId;
    }
    if (row.noScheduleInspection) {
      return 'No Schedule';
    }
    return '';
  }

  getApplicationNumberDisplay(row: UploadInspectionRequestViewModel): string {
    return row.applicationNumber || '';
  }

  getAttachmentsDisplay(row: UploadInspectionRequestViewModel): string {
    if (!row.attachmentsJSON || row.attachmentsJSON.trim().length === 0) {
      return 'None';
    }
    return 'View';
  }

  getSignaturesDisplay(row: UploadInspectionRequestViewModel): string {
    if ((!row.inspectorSignatureImageJSON || row.inspectorSignatureImageJSON.trim().length === 0) &&
        (!row.picSignatureImageJSON || row.picSignatureImageJSON.trim().length === 0)) {
      return 'None';
    }
    return 'View';
  }

  openAttachmentsModal(row: UploadInspectionRequestViewModel): void {
    if (!row.attachmentsJSON || row.attachmentsJSON.trim().length === 0) {
      return;
    }
    try {
      const parsed = JSON.parse(row.attachmentsJSON);
      // Keep as array of objects
      if (Array.isArray(parsed)) {
        this.selectedAttachments = parsed;
      } else {
        this.selectedAttachments = [parsed];
      }
      this.showAttachmentsModal = true;
    } catch (e) {
      console.error('Error parsing attachments JSON:', e, 'Raw JSON:', row.attachmentsJSON);
      this.selectedAttachments = [];
      this.showAttachmentsModal = true;
    }
  }

  openSignaturesModal(row: UploadInspectionRequestViewModel): void {
    if ((!row.inspectorSignatureImageJSON || row.inspectorSignatureImageJSON.trim().length === 0) &&
        (!row.picSignatureImageJSON || row.picSignatureImageJSON.trim().length === 0)) {
      return;
    }
    this.selectedSignatures = [];
    try {
      if (row.inspectorSignatureImageJSON && row.inspectorSignatureImageJSON.trim().length > 0) {
        const inspectorSigs = JSON.parse(row.inspectorSignatureImageJSON);
        if (Array.isArray(inspectorSigs)) {
          this.selectedSignatures.push(...inspectorSigs);
        } else {
          this.selectedSignatures.push(inspectorSigs);
        }
      }
    } catch (e) {
      console.error('Error parsing inspector signature JSON:', e);
    }
    try {
      if (row.picSignatureImageJSON && row.picSignatureImageJSON.trim().length > 0) {
        const picSigs = JSON.parse(row.picSignatureImageJSON);
        if (Array.isArray(picSigs)) {
          this.selectedSignatures.push(...picSigs);
        } else {
          this.selectedSignatures.push(picSigs);
        }
      }
    } catch (e) {
      console.error('Error parsing PIC signature JSON:', e);
    }
    this.showSignaturesModal = true;
  }

  openDetailsModal(row: UploadInspectionRequestViewModel): void {
    this.selectedDetails = row;
    this.showDetailsModal = true;
  }

  openResultsModal(row: UploadInspectionRequestViewModel): void {
    if (!row.resultsJSON || row.resultsJSON.trim().length === 0) {
      return;
    }
    try {
      this.selectedResults = JSON.parse(row.resultsJSON);
      this.showResultsModal = true;
    } catch (e) {
      console.error('Error parsing results JSON:', e);
      this.selectedResults = row.resultsJSON;
      this.showResultsModal = true;
    }
  }

  openErrorModal(row: UploadInspectionRequestViewModel): void {
    if (!row.scheduledTaskErrorMessage) {
      return;
    }
    this.selectedError = row.scheduledTaskErrorMessage;
    this.showErrorModal = true;
  }

  closeAttachmentsModal(): void {
    this.showAttachmentsModal = false;
    this.selectedAttachments = [];
  }

  closeSignaturesModal(): void {
    this.showSignaturesModal = false;
    this.selectedSignatures = [];
  }

  closeDetailsModal(): void {
    this.showDetailsModal = false;
    this.selectedDetails = null;
  }

  closeResultsModal(): void {
    this.showResultsModal = false;
    this.selectedResults = null;
  }

  closeErrorModal(): void {
    this.showErrorModal = false;
    this.selectedError = '';
  }

  onRefresh(): void {
    this.currentPage = 0;
    this.loadData();
    this.loadRecordCount();
  }
}
